import React, { ReactText, useCallback, useEffect, useState } from "react";
import { Button } from "antd";
import moment from "moment";
import { inject, observer } from "mobx-react";
import { ColumnsType } from "antd/lib/table/interface";
import { DateRangeDate, IObjectMap } from "../interfaces/common";
import TableWrapper from "../components/declarants/tableWrapper";
import Table from "../components/common/Table";
import COLUMNS from "../components/tables/departmentData/config";
import DepartmentsDataStore from "../stores/pages/DepartmentsDataStore";
import Settings from "../components/declarants/Settings";
import {
  DECLARANT_DATA_ACTIVE_COLUMNS,
  DECLARANT_DATA_ORDER_COLUMNS,
} from "../constants/settings";
import DateRange from "../components/declarants/dateRange";
import updateSortingAndFiltersOfColumns from "../helpers/filters";
import Summary from "../components/tables/departmentData/summaries/summary";
import SelectFilter from "../components/Grid/headers/SelectFilter";
import ActionButton from "../components/declarants/groupButton";
import {
  EXPORT_TO_EXCEL,
  JOIN_BUTTON,
  CUSTOM_EXPORT_TO_EXCEL,
  GROUP_BUTTON_DEPARTMENTS_DATA,
  UNGROUP_BUTTON_DEPARTMENTS_DATA,
  GENERATE_INVOICE_BUTTON,
  GENERATE_REGISTER_OF_ACCOUNTS_BUTTON,
} from "../constants/translates";
import { IHasChildren } from "../components/common/Table/Interfaces";
import { Declarant } from "../models/Declarant";
import CollapseHeader from "../components/CollapseHeader";
import Card from "../components/Card/Card";
import SettingsStore from "../stores/SettingsStore";
import UserStore from "../stores/UserStore";
import RefreshButton from "../components/buttons/RefreshButton";
import { CUSTOM_EXPORT_TO_EXCEL_COLUMNS } from "../constants/exports";
import GenerateInvoiceModal from "../components/modals/GenerateInvoiceModal/GenerateInvoiceModal";
import { getDepartmentDataStore } from "../stores/containers/DepartmentDataStore.container";
import {
  DepartmentSource,
  isDepartmentSource,
} from "../models/DepartmentData/DepartmentSource";
import Download from "../components/declarants/download";
import { ModalHandler, useExecuteModal } from "../hooks/useExecuteModal";
import DateRangeWarningModal from "../components/tables/departmentData/modals/DateRangeWarningModal";

let isLoaded = false;

const DepartmentsDataPage: React.FC<{
  settingsStore: SettingsStore;
  userStore: UserStore;
}> = ({ settingsStore, userStore }) => {
  const departmentsDataStore: DepartmentsDataStore = getDepartmentDataStore();
  const generateInvoiceModalHandler: ModalHandler = useExecuteModal();
  const dateRangeWarningModalHandler: ModalHandler = useExecuteModal();

  const [pendingStartDate, setPendingStartDate] = useState<DateRangeDate>(
    departmentsDataStore.parameters.start_date as DateRangeDate
  );
  const [pendingEndDate, setPendingEndDate] = useState<DateRangeDate>(
    departmentsDataStore.parameters.end_date as DateRangeDate
  );
  const [pendingClientId, setPendingClientId] = useState<number | string>(
    (departmentsDataStore.parameters.client as number | string) ?? "all"
  );
  const [pendingSource, setPendingSource] = useState<DepartmentSource | string>(
    isDepartmentSource(departmentsDataStore.parameters.source)
      ? departmentsDataStore.parameters.source
      : departmentsDataStore.defaultSource
  );

  const [filtersTouched, setFiltersTouched] = useState<boolean>(true);

  useEffect(() => {
    if (isLoaded) return;

    departmentsDataStore.load({});
    isLoaded = true;
  }, [departmentsDataStore]);

  const onLoadHandler = useCallback(
    (parameters?: IObjectMap, forceUpdate?: boolean): void => {
      departmentsDataStore.load(
        {
          ...parameters,
          start_date: departmentsDataStore.parameters.start_date,
          end_date: departmentsDataStore.parameters.end_date,
        },
        forceUpdate
      );
    },
    [departmentsDataStore]
  );

  const downloadExcelFIle = () => {
    const activeColumnKey = settingsStore.columns
      .filter((column) => column.isActive)
      .map((column) => {
        return { [column.key]: column.title };
      });

    departmentsDataStore.downloadExcel({
      activeColumn: activeColumnKey,
      ...departmentsDataStore.parameters,
    });
  };

  const executeLoad = useCallback(() => {
    const clientFilter = pendingClientId !== "all" ? pendingClientId : null;
    departmentsDataStore.load(
      {
        page: 1,
        start_date: pendingStartDate,
        end_date: pendingEndDate,
        client: clientFilter,
        source: pendingSource,
      },
      true
    );
    setFiltersTouched(false);
  }, [
    pendingStartDate,
    pendingEndDate,
    pendingClientId,
    pendingSource,
    departmentsDataStore,
  ]);

  const applyFilters = useCallback(() => {
    const start = pendingStartDate
      ? moment(pendingStartDate as moment.MomentInput)
      : null;
    const end = pendingEndDate
      ? moment(pendingEndDate as moment.MomentInput)
      : null;

    if (start && end && end.diff(start, "months", true) > 1) {
      dateRangeWarningModalHandler.executeModal();
      return;
    }

    executeLoad();
  }, [
    pendingStartDate,
    pendingEndDate,
    executeLoad,
    dateRangeWarningModalHandler,
  ]);

  const selectClientHandler = (clientId: number | string) => {
    setPendingClientId(clientId);
  };

  const selectSourceHandler = (source: DepartmentSource): void => {
    setPendingSource(source);
  };

  const filterClientItems = [
    { title: "All Clients", value: "all" },
    ...(settingsStore.clientsWithNoClients || []),
  ];

  const currentFilter =
    settingsStore.clientsWithNoClients.find(
      ({ value }) => value === departmentsDataStore.parameters.client
    )?.title || "all";

  const downloadCustomExcelFile = useCallback((): void => {
    departmentsDataStore.downloadCustomExcel({
      columns: CUSTOM_EXPORT_TO_EXCEL_COLUMNS,
      ...departmentsDataStore.parameters,
    });
  }, [departmentsDataStore]);

  useEffect(() => {
    setFiltersTouched(true);
  }, [pendingStartDate, pendingEndDate, pendingClientId, pendingSource]);

  useEffect(() => {
    settingsStore?.setColumns(
      updateSortingAndFiltersOfColumns(
        userStore.authorizedOnly(COLUMNS) || COLUMNS,
        departmentsDataStore.parameters as IObjectMap
      ),
      DECLARANT_DATA_ORDER_COLUMNS,
      DECLARANT_DATA_ACTIVE_COLUMNS
    );
  }, [
    settingsStore,
    settingsStore.settings,
    userStore,
    userStore.user,
    settingsStore.isEnabledReorder,
    departmentsDataStore.parameters,
  ]);

  return (
    <>
      <CollapseHeader>
        <DateRange
          onChange={(startDate, endDate) => {
            setPendingStartDate(startDate);
            setPendingEndDate(endDate);
          }}
          startDate={pendingStartDate}
          endDate={pendingEndDate}
          allowClear={false}
        />
        <SelectFilter
          items={filterClientItems}
          onChange={(id) => selectClientHandler(id as number)}
          defaultValue={currentFilter}
        />
        <SelectFilter
          items={departmentsDataStore.sourceOptions}
          onChange={selectSourceHandler}
          defaultValue={
            isDepartmentSource(departmentsDataStore.parameters.source)
              ? departmentsDataStore.parameters.source
              : departmentsDataStore.defaultSource
          }
        />
        <Button
          type="primary"
          onClick={applyFilters}
          disabled={departmentsDataStore.isLoadingTableData || !filtersTouched}
          className="filter-button"
        >
          Apply Filter
        </Button>
        <Summary store={departmentsDataStore} />
      </CollapseHeader>
      <Card>
        <Card.Header>
          <Download store={departmentsDataStore} />
          <ActionButton
            onClick={downloadExcelFIle}
            disabled={false}
            text={EXPORT_TO_EXCEL}
          />
          <ActionButton
            onClick={downloadCustomExcelFile}
            disabled={departmentsDataStore.isDisabledCustomExportButton}
            text={CUSTOM_EXPORT_TO_EXCEL}
          />
          <ActionButton
            onClick={generateInvoiceModalHandler.executeModal}
            disabled={departmentsDataStore.isDisabledInvoiceButton}
            text={GENERATE_INVOICE_BUTTON}
          />
          <ActionButton
            onClick={() => departmentsDataStore.generateRegisterOfAccounts()}
            loadingOnProcess
            disabled={departmentsDataStore.isDisabledRegisterAccountsButton}
            text={GENERATE_REGISTER_OF_ACCOUNTS_BUTTON}
          />
          <ActionButton
            disabled={departmentsDataStore.isDisabledGroupButton}
            onClick={() =>
              departmentsDataStore.makeCustomGroup(departmentsDataStore)
            }
            text="+"
            tooltip
            tooltipProps={{
              title: GROUP_BUTTON_DEPARTMENTS_DATA,
              placement: "top",
            }}
          />
          <ActionButton
            onClick={() =>
              departmentsDataStore.makeUngroup(departmentsDataStore)
            }
            disabled={departmentsDataStore.isDisabledUngroupButton}
            text="-"
            tooltip
            tooltipProps={{
              title: UNGROUP_BUTTON_DEPARTMENTS_DATA,
              placement: "top",
            }}
          />
          <ActionButton
            onClick={() => departmentsDataStore.joinGroup()}
            disabled={departmentsDataStore.isDisabledJoinButton}
            text={JOIN_BUTTON}
            tooltip
            tooltipProps={{
              title: "Add rows to the group",
              placement: "top",
            }}
          />
          <RefreshButton refresh={departmentsDataStore} />
          <Settings reorderable />
        </Card.Header>
        <TableWrapper enableScrollBar={settingsStore.isEnabledScrollbar}>
          <Table
            columns={settingsStore.activeColumns as ColumnsType<Declarant>}
            settingsStore={settingsStore}
            data={departmentsDataStore.data}
            onLoad={onLoadHandler}
            hasMoreItems={departmentsDataStore.hasMoreItems}
            isLoading={departmentsDataStore.isLoadingTableData}
            onRowSelectors={(items: ReactText[]) => {
              departmentsDataStore.changeSelectedRows(items);
            }}
            checkStrictly={false}
            selectedRows={departmentsDataStore.selectedRows}
            rowClassName={(record: object): string => {
              if ((record as IHasChildren<Declarant>).children) {
                return "expandable-row";
              }
              return "non-expandable-row";
            }}
            onExpandedRowsChange={(expandedRows) => {
              departmentsDataStore.expandedRows = expandedRows;
            }}
            expandedRows={departmentsDataStore.expandedRows}
          />
        </TableWrapper>
      </Card>
      <GenerateInvoiceModal
        handler={generateInvoiceModalHandler}
        store={departmentsDataStore}
        client={departmentsDataStore.selectedGroupClient}
      />
      <DateRangeWarningModal
        onApply={executeLoad}
        handler={dateRangeWarningModalHandler}
      />
    </>
  );
};

export default inject(({ store }) => ({
  settingsStore: store.settingsStore,
  userStore: store.userStore,
}))(observer(DepartmentsDataPage));
